import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactAudioPlayer from 'react-audio-player';
import { calculateSize, formatParagraphs, getPodcastImage } from '../../../models/Utils';
import { setMediaState } from '../../../store/reducers/mainReducer';
import Button from '../../Common/Button/Button';

import AudioBase from '../AudioBase/AudioBase';
import MediaControls from '../../Common/MediaControls/MediaControls';
import VTTReader from '../../Common/VTTReader/VTTReader';
import PlayButton from '../../../assets/images/buttons/btn_play.svg';
import PauseButton from '../../../assets/images/buttons/btn_pause.svg';
import { ADAARIARoles, ADAWidgitRoles, ADALandmarks } from '../../../models/ADA';

import './AudioClip.scss';

const AudioClip = ({ isActive, cardData, isInline, size }) => {
  const dispatch = useDispatch();
  const [fullscreen, goToFullscreen] = useState(false);
  const activeKey = useSelector(state => state.data.activeSlideKey);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [VTTCaptions, setVTTCaptions] = useState(null)
  const menuOpened = useSelector(state => state.data.menuOpened);
  const updateIntervalMs = 50;
  const audioRef = useRef(null);
  const mediaPath = useSelector(state => state.config.basePath + state.config.apiMediaPath);

  useEffect(() => {
    if(activeKey === cardData.Key){
      reset();
      fetch(mediaPath + cardData.VTTCaptions)
      .then(res => res.blob())
        .then(result => result.text())
      .then( vtt =>{
        setIsLoaded(true);
        setVTTCaptions(vtt);
      });
  
    } else {
      setPlaying(false);
      setMuted(true);
      goToFullscreen(false);
    }
  }, [activeKey]);

  useEffect(() => {
    dispatch(setMediaState(playing));
  }, [playing]);


  const handleButtonClicked = data => {
    if(playing){
      goToFullscreen(false);
      setPlaying(false);
      pause();
    } else {
      goToFullscreen(true);
      setPlaying(true);
      setMuted(false);
      play();
    }
    seek(0);
  };

  const reset = prop => {
    setPlaying(false);
    pause();
    setMuted(true);
    goToFullscreen(false);
  };

  const togglePlay = prop => {
    if (playing) {
      pause();
    } else {
      play();
    }
  };

  const play = prop => {
    if (
      audioRef &&
      audioRef.current &&
      audioRef.current.audioEl &&
      audioRef.current.audioEl.current
    ) {
      audioRef.current.audioEl.current.play();
    }
  };

  const pause = prop => {
    if (
      audioRef &&
      audioRef.current &&
      audioRef.current.audioEl &&
      audioRef.current.audioEl.current
    ) {
      audioRef.current.audioEl.current.pause();
    }
  };

  const seek = val => {
    if (
      audioRef &&
      audioRef.current &&
      audioRef.current.audioEl &&
      audioRef.current.audioEl.current
    ) {
      audioRef.current.audioEl.current.currentTime = val * duration;
      setProgress(val)
    }
  };

  /* Control Events */
  const handlePlayClicked = prop => {
    togglePlay();
    if(muted)
      setMuted(false);
    setPlaying(!playing);
  };

  const handleMuteClicked = prop => {
    setMuted(!muted);
  };

  const handleMinimizeClicked = prop => {
    reset();
  };

  const handleSliderClicked = prop => {
    seek(prop);
  };

  /* Audio Events */
  const handleProgress = seconds => {
    setProgress(seconds / duration);
  };

  const handleDuration = prop => {
    if (
      audioRef &&
      audioRef.current &&
      audioRef.current.audioEl &&
      audioRef.current.audioEl.current
    ) {
      var duration = audioRef.current.audioEl.current.duration;
      setDuration(duration);
    }
  };

  const handleOnEnded = prop => {
    reset();
  };

  const handleSubscribeClicked = data => {
    window.open(cardData.SubscribeURL, '_blank');
  };

  var dimensions = calculateSize(1920, 1080, size.width, size.height);

  var src = cardData.ListenURL ? cardData.ListenURL : mediaPath + cardData.AudioClip;

  var audio = (
    <ReactAudioPlayer
      ref={audioRef}
      autoPlay={playing}
      className="audio"
      src={src}
      muted={muted}
      listenInterval={updateIntervalMs}
      onListen={handleProgress}
      onLoadedMetadata={handleDuration}
      onEnded={handleOnEnded}
      style={{width:dimensions.width, height:dimensions.height}}
    />
  );

  var podcastImage = getPodcastImage(cardData);
  var opacityStyle = {
    opacity: podcastImage.Opacity ? podcastImage.Opacity : 1,
  };
  var classname = " audio-wrapper " + (isInline ? "inline" : "");

  var captions = VTTCaptions ? <VTTReader raw={VTTCaptions} progress={progress} duration={duration} /> : null
  var controls = <MediaControls
    playing={playing}
    muted={muted}
    progress={progress}
    duration={duration}
    updateInterval={updateIntervalMs}
    onPlay={handlePlayClicked}
    onMute={handleMuteClicked}
    onScrub={handleSliderClicked}
    onMinimize={handleMinimizeClicked}
    captions={captions}
  />;

  return (
    <div className={"carousel-img has-video" + classname} role={ADALandmarks.Main}>
      {
        (<>
        <div className="width-wrapper">
            <div className="content-wrapper">
              
            {cardData.Header? <h3 role={ADAARIARoles.Heading} >{cardData.Header}</h3> : null}
            
            <div className={cardData.Header ? 'body' : 'no-header'} >
              {formatParagraphs(cardData.Subheader)}
            </div>

              {cardData.SubscribeURL ?
                <div className="button-holder">
                  <Button
                    children={'Subscribe'}
                    buttonStyle="btn--outline"
                    buttonSize="btn--medium"
                    buttonColor="primary"
                    onClick={handleSubscribeClicked}
                    tabIndex={!menuOpened ? 0 : -1}
                  />
                </div>
                :null
              }
          </div>
        </div>
        <AudioBase active={isActive} cardData={cardData} onPlay={handlePlayClicked} controls={controls} />
          
          {audio}
        
        </>
      )}
      
      {/* <>
        <div className="audio-placeholder" style={opacityStyle}>
          <div role="img" alt={getPodcastImage(cardData).AltText}>
            <img src={getPodcastImage(cardData).Image}/>
            {audio}
          </div>
        </div>
        <AudioBase type="Podcast" title={cardData.Header} subtitle={cardData.Subheader} />
          <div className="video-touch-blocker" onClick={handleMinimizeClicked} tabIndex={!menuOpened ? 0 : -1} aria-label="Play" role={ADAWidgitRoles.Button}>
            <div className="ada-hidden">Play button</div>
          </div>
        </> */}
      
      {/* <div className="audio-content">
        <AudioBase type="Podcast" title={cardData.Header} subtitle={cardData.Subheader} />
        <button className="play-button" onClick={handleButtonClicked}>
          <img className="play-button-img" src={(playing)?PauseButton:PlayButton} alt="Pause/Play Toggle Button" />
        </button>
      </div> 
      {audio}
      {fullscreen ? (
        <MediaControls
          playing={playing}
          muted={muted}
          progress={progress}
          duration={duration}
          updateInterval={updateIntervalMs}
          onPlay={handlePlayClicked}
          onMute={handleMuteClicked}
          onScrub={handleSliderClicked}
          onMinimize={handleMinimizeClicked}
        />
      ) : null}*/}
    </div>
  );
};

export default AudioClip;
